import { chatbotParamsDefaultValues } from "@do/shared-types";
import { MotionConfig } from "motion/react";
import { useTranslations } from "next-intl";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { MAX_USER_MESSAGE_LENGTH, MIN_CHAT_VIEWPORT_HEIGHT_PX } from "../constants";
import { filterRasaCommand } from "../lib/filter-rasa-command";
import { mergeBotMessages } from "../lib/merge-bot-messages";
import { getLayoutScrollBottom, scrollElementToTop } from "../lib/scroll-element-to-top";
import { ChatProvider, type ChatContextType } from "../providers/chat-provider";
import { ChatContainer } from "./chat-container";
import { CSSVariables } from "./css-variables";
import { Fonts } from "./fonts";
import { Footer } from "./footer";
import { Header } from "./header";
import { Messages } from "./messages";

export type ChatProps = Omit<
  ChatContextType,
  | "scrollToBottom"
  | "isAtBottom"
  | "setIsAtBottom"
  | "messagesRef"
  | "lastMessageRef"
  | "footerContent"
  | "setFooterContent"
  | "buttonsContent"
  | "setButtonsContent"
  | "canBackToPreviousStep"
  | "handleBackToPreviousStep"
  | "getMessageResetKey"
  | "messagesListRef"
  | "lastClickedButton"
  | "setLastClickedButton"
  | "pendingCallConfirmation"
  | "onConfirmCall"
  | "onCancelCall"
> & {
  cdnUrl: string;
};

export const Chat = ({
  autoScroll = true,
  cdnUrl,
  chatbotParams,
  messages,
  onFastAction,
  onSubmit,
  isClosable = false,
  isFullSize = false,
  isOutOfCredits = false,
  isPreviewMode = false,
  isTestingMode = false,
  isEditingMode = false,
  isLoading = false,
  isMobile,
  lastUserMessageId,
  lastUserMessageRef,
  maxUserMessageLength = MAX_USER_MESSAGE_LENGTH,
  ...props
}: ChatProps) => {
  const t = useTranslations("do-chat-v2");
  const [isAtBottom, setIsAtBottom] = useState(true);
  const [isViewportTooShort, setIsViewportTooShort] = useState(false);
  const [pendingCallConfirmation, setPendingCallConfirmation] =
    useState<ChatContextType["pendingCallConfirmation"]>(null);
  const layoutViewportHeightRef = useRef<number | null>(null);

  const messagesRef = useRef<HTMLDivElement>(null);
  const lastMessageRef = useRef<HTMLDivElement>(null);
  const messagesListRef = useRef<HTMLDivElement>(null);
  // Track if we just clicked a fastAction to prevent auto-scroll override
  const justClickedFastActionRef = useRef(false);

  const mergedChatbotParams = {
    ...chatbotParamsDefaultValues,
    ...chatbotParams,
    ...(isFullSize && chatbotParams?.fullSize ? chatbotParams.fullSize : {}),
  };

  const filteredMessages = useMemo(() => {
    const allMessages = messages.filter(filterRasaCommand);
    return mergeBotMessages(allMessages);
  }, [messages]);

  useEffect(() => {
    const update = () => {
      const currentHeight = window.innerHeight;

      if (isMobile) {
        // Mobile browsers shrink `innerHeight` when the virtual keyboard opens.
        // Keep the largest seen height so the keyboard does not flip the chat into
        // "viewport too small" and unmount the footer input.
        layoutViewportHeightRef.current =
          layoutViewportHeightRef.current === null
            ? currentHeight
            : Math.max(layoutViewportHeightRef.current, currentHeight);
        setIsViewportTooShort(layoutViewportHeightRef.current < MIN_CHAT_VIEWPORT_HEIGHT_PX);
        return;
      }

      layoutViewportHeightRef.current = currentHeight;
      setIsViewportTooShort(currentHeight < MIN_CHAT_VIEWPORT_HEIGHT_PX);
    };

    const onOrientationChange = () => {
      layoutViewportHeightRef.current = window.innerHeight;
      update();
    };

    update();
    window.addEventListener("resize", update, { passive: true });
    window.addEventListener("orientationchange", onOrientationChange, { passive: true });
    return () => {
      window.removeEventListener("resize", update);
      window.removeEventListener("orientationchange", onOrientationChange);
    };
  }, [isMobile]);

  const scrollToBottom = useCallback(
    (params?: { force?: boolean; smooth?: boolean }) => {
      if (mergedChatbotParams.scrollOnTopOfUserMessages) return;
      const { force = false, smooth = false } = params || {};

      if (!messagesRef.current) return;

      // Respect special "top of last message" mode (except on very first open handled below)
      if (
        !force &&
        mergedChatbotParams.lastMessageScrollPosition === "top" &&
        lastMessageRef.current
      ) {
        scrollElementToTop(messagesRef.current, lastMessageRef.current, smooth ? "smooth" : "auto");
        return;
      }

      void messagesRef.current.scrollTo({
        behavior: smooth ? "smooth" : "auto",
        top: messagesListRef.current
          ? getLayoutScrollBottom(messagesRef.current, messagesListRef.current)
          : messagesRef.current.scrollHeight,
      });
    },
    [mergedChatbotParams.lastMessageScrollPosition, mergedChatbotParams.scrollOnTopOfUserMessages]
  );

  const scrollAfterFastAction = useCallback(() => {
    justClickedFastActionRef.current = true;
    setTimeout(() => {
      scrollToBottom({ force: true });
      setTimeout(() => {
        justClickedFastActionRef.current = false;
      }, 500);
    }, 0);
  }, [scrollToBottom]);

  // Desktop calls require an inline confirmation before reaching the host.
  const handleFastAction: ChatContextType["onFastAction"] = useCallback(
    (resp) => {
      if (resp.tel && !isMobile) {
        setPendingCallConfirmation(resp);
      } else {
        setPendingCallConfirmation(null);
        onFastAction(resp);
      }

      scrollAfterFastAction();
    },
    [isMobile, onFastAction, scrollAfterFastAction]
  );

  const handleConfirmCall = useCallback(() => {
    if (!pendingCallConfirmation) return;

    setPendingCallConfirmation(null);
    onFastAction(pendingCallConfirmation);
    scrollAfterFastAction();
  }, [onFastAction, pendingCallConfirmation, scrollAfterFastAction]);

  const handleCancelCall = useCallback(() => {
    setPendingCallConfirmation(null);
  }, []);

  const handleSubmit = useCallback(
    (message: string) => {
      setPendingCallConfirmation(null);
      onSubmit(message);
      // Wait for the message to be rendered before scrolling
      // Use a small delay to ensure React has updated the DOM
      setTimeout(() => {
        scrollToBottom({ force: true });
      }, 50);
    },
    [scrollToBottom, onSubmit]
  );

  return (
    <ChatProvider
      {...props}
      scrollToBottom={scrollToBottom}
      onFastAction={handleFastAction}
      onSubmit={handleSubmit}
      pendingCallConfirmation={pendingCallConfirmation}
      onConfirmCall={handleConfirmCall}
      onCancelCall={handleCancelCall}
      messagesRef={messagesRef}
      messagesListRef={messagesListRef}
      lastMessageRef={lastMessageRef}
      lastUserMessageId={lastUserMessageId}
      lastUserMessageRef={lastUserMessageRef}
      autoScroll={autoScroll}
      isClosable={isClosable}
      isFullSize={isFullSize}
      isOutOfCredits={isOutOfCredits}
      isPreviewMode={isPreviewMode}
      isTestingMode={isTestingMode}
      isEditingMode={isEditingMode}
      isLoading={isLoading}
      maxUserMessageLength={maxUserMessageLength}
      isAtBottom={isAtBottom}
      setIsAtBottom={setIsAtBottom}
      messages={filteredMessages}
      chatbotParams={mergedChatbotParams}
    >
      <Fonts cdnUrl={cdnUrl} chatbotParams={mergedChatbotParams} />

      <CSSVariables chatbotParams={mergedChatbotParams} />

      <MotionConfig reducedMotion="user">
        <ChatContainer
          aria-labelledby={!props.noHeader && !isViewportTooShort ? "header-title" : undefined}
          aria-label={isViewportTooShort ? t("chat.viewport-too-small") : undefined}
        >
          {isViewportTooShort ? (
            <div
              id="chat-viewport-too-small"
              role="status"
              aria-live="polite"
              className="flex-1 min-h-0 flex items-center justify-center p-4 text-center"
            >
              <div className="max-w-prose">
                <p className="font-semibold text-balance text-neutral-600">
                  {t("chat.viewport-too-small")}
                </p>
              </div>
            </div>
          ) : (
            <>
              {!props.noHeader && !mergedChatbotParams?.noHeader && <Header />}

              <main id="main" className="flex-1 min-h-0 flex">
                <Messages justClickedFastActionRef={justClickedFastActionRef} />
              </main>

              <Footer
                disabledInput={filteredMessages[filteredMessages.length - 1]?.events?.disabledInput}
              />
            </>
          )}
        </ChatContainer>
      </MotionConfig>
    </ChatProvider>
  );
};
